import OctopusIcon from '@assets/images/icons/octopus/pricing-enterprise.svg?react'
import * as ModalTypes from '@components/App/Modal/constants'
import { Switcher } from '@lib/components'
import type {
  CheckoutPayload,
  StripeRedirect,
} from '@octopus-do/octopus-api-client'
import { Button, ThemeColors } from '@octopus-do/octopus-storybook'
import { ArrowRightArrowLeft } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SubscriptionActions } from '@store/reducers/subscription'
import * as ActionTypes from '@storeOld/actions'
import { ContactSupportErrorNotification } from '@storeOld/reducers/notification'
import cx from 'classnames'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  PLAN_AGENCY,
  PLAN_ANNUAL,
  PLAN_MONTHLY,
} from '@/constants/userSubscriptionPlans'
import { NestClient } from '@/nest'
import { OpenConfirmation } from '@/store'
import type { AppState, AuthState, ModalProps, UiState } from '@/types'
import { ModalHeader } from '../../Modal/components/ModalHeader'
import RecaptchaWidget from '../../RecaptchaWidget'
import { AgencyPlan } from './AgencyPlan'
import { Plan } from './Plan'
import { planTypes } from './planTypes'
import css from './pricing.module.scss'

export const Pricing: React.FC<ModalProps> = ({ onClose }) => {
  const dispatch = useDispatch()
  const { modalProps } = useSelector<AppState, UiState>(({ ui }) => ui)
  const {
    planId: authPlanId,
    planType: authPlanType,
    authenticated,
    paymentProvider,
  } = useSelector<AppState, AuthState>(({ auth }) => auth)

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const [planType, setType] = useState(modalProps.planType || authPlanType)
  const [loadingId, setLoadingId] = useState<null | string>(null)

  const toggleItems = useMemo(() => {
    return [
      {
        content: 'Monthly',
        checked: PLAN_MONTHLY === planType,
      },
      {
        content: 'Annual',
        checked: PLAN_ANNUAL === planType,
      },
    ]
  }, [planType])

  const types = Object.keys(planTypes)
  const planIndex = types.indexOf(authPlanId)

  const handleSwitch = (index: number) => {
    if (toggleItems[0]) {
      setType(toggleItems[index].content.toLowerCase())
    }
  }

  const onSubscribeClick = (planId: string) => {
    if (!authenticated) {
      const redirectAfterAuth = `/subscription/checkout/?planId=${planId}&planType=${planType}`

      dispatch({
        type: ActionTypes.OPEN_MODAL,
        payload: {
          modalType: ModalTypes.MODAL_AUTH,
          modalProps: { authType: 'signup', redirectAfterAuth },
        },
      })

      return
    }

    if (paymentProvider === 'braintree') {
      dispatch(
        OpenConfirmation({
          message: 'To switch plan, please update payment details.',
          label: 'Update',
          title: 'Update payment information',
          then: [SubscriptionActions.requestBillingPortal()],
        })
      )
    } else {
      setLoadingId(planId)

      const couponCode = window.sessionStorage.getItem('coupon_code')

      const payload: CheckoutPayload = {
        planId: planId as CheckoutPayload['planId'],
        planType: planType as CheckoutPayload['planType'],
        ...(couponCode && { couponCode }),
        ...(projectId && { saveProject: projectId }),
      }

      NestClient.subscription
        .checkout(payload)
        .then(({ url }: StripeRedirect) => {
          window.location.href = url
        })
        .catch((e) => {
          setLoadingId(null)
          dispatch(ContactSupportErrorNotification)
          console.error(e)
        })
    }
  }

  const openComparePage = () => {
    window.location.assign('/sitemap/pricing')
  }

  const openContactsPage = () => {
    window.location.assign('/sitemap/support/contacts')
  }

  return (
    <div className={css.container}>
      <ModalHeader title="" onClose={onClose} />

      <div className={css.body}>
        <div className={css.header}>
          <h3 className={css.title}>Pricing plans</h3>

          <div className={css.switcher}>
            <Switcher
              size="medium"
              items={toggleItems}
              onClick={handleSwitch}
            />
            <span className={css.text}>Save up to 20%</span>
          </div>
        </div>

        <RecaptchaWidget>
          <Plan
            planType={planType}
            planId={authPlanId}
            loadingId={loadingId}
            planIndex={planIndex}
            types={types}
            onSubscribeClick={onSubscribeClick}
          />
        </RecaptchaWidget>

        <Button
          className={css.compareBtn}
          priority="secondary"
          label="Compare features"
          left={ArrowRightArrowLeft}
          onClick={openComparePage}
        />

        {authPlanId === PLAN_AGENCY && (
          <RecaptchaWidget>
            <AgencyPlan
              planType={planType}
              planId={authPlanId}
              loadingId={loadingId}
              planIndex={planIndex}
              types={types}
              onSubscribeClick={onSubscribeClick}
              {...planTypes[authPlanId]}
            />
          </RecaptchaWidget>
        )}

        {authPlanId !== PLAN_AGENCY && <div className={css.divider} />}

        <a
          href="/sitemap/support/contacts"
          className={cx(css.discount, css.defaultText, {
            [css.mt]: authPlanId === PLAN_AGENCY,
          })}
        >
          <i className="fa fa-graduation-cap" />
          Special discount for students and educational organisations
          <i className="fa fa-angle-right" />
        </a>

        <div className={css.enterprise}>
          <div className={css.icon}>
            <OctopusIcon />
          </div>
          <div className={css.textWrapper}>
            <h4 className={css.enterpriseTitle}>Enterprise</h4>
            <p className={cx(css.enterpriseDescription, css.defaultText)}>
              For larger companies with complex needs and controls.
              <br />
              Advanced tools for security.
            </p>
          </div>
          <Button
            className={cx(css.contactBtn, ThemeColors.dark)}
            priority="secondary"
            size="medium"
            label="Contact us"
            onClick={openContactsPage}
          />
        </div>
      </div>
    </div>
  )
}
